import { useState, useEffect, useCallback } from 'react';
import { Store } from '../../../types';
import { getAllStores, getAllStoreControls } from '../../../services/api';

// Store statuses that are explicitly selectable for bulk operations. The status
// is joined from the Store Control dataset; only OPN/PRE stores qualify. (If
// that source is sparse and stores are missing, that's a data issue to fix at
// the source — we keep the FE truthful rather than guess.)
const SELECTABLE_STORE_STATUSES = ['OPN', 'PRE'];
// Valid franchise (FZ) codes are exactly 3 characters.
const VALID_FRANCHISE_CODE_LENGTH = 3;
// Test-store franchise code: always shown regardless of status.
const TEST_FRANCHISE_CODE = 'TES';

/**
 * Joins raw stores with their Store Control status, then keeps stores in a
 * 3-character franchise code that are either in the TES (test) franchise — which
 * is always available — or have an explicit OPN/PRE status. The result is sorted
 * by franchise code (then store number) so the selectors list FZs in order.
 */
const filterAndSortStores = (
  stores: Store[],
  statusByStoreNo: Map<string, string | undefined>
): Store[] => {
  return stores
    .map((store) => ({ ...store, storeStatus: statusByStoreNo.get(store.storeNumber) }))
    .filter((store) => {
      if (store.franchiseCode?.length !== VALID_FRANCHISE_CODE_LENGTH) return false;
      if (store.franchiseCode === TEST_FRANCHISE_CODE) return true; // test stores always shown
      const status = store.storeStatus?.trim().toUpperCase();
      return !!status && SELECTABLE_STORE_STATUSES.includes(status);
    })
    .sort((a, b) => {
      const byFranchise = (a.franchiseCode || '').localeCompare(b.franchiseCode || '');
      return byFranchise !== 0 ? byFranchise : a.storeNumber.localeCompare(b.storeNumber);
    });
};

// How long cached store data stays fresh before a background refresh. The
// cache persists across navigation, so returning to the page within the TTL is
// instant with no refetch.
const CACHE_TTL_MS = 60 * 60 * 1000; // 60 minutes

// Shared cache for stores data
let storesCache: {
  data: Store[] | null;
  isLoading: boolean; // true only on a cold load (no data yet) — drives spinners
  isFetching: boolean; // true whenever a request is in flight (guards duplicates)
  error: any;
  fetchedAt: number | null;
  subscribers: Set<() => void>;
} = {
  data: null,
  isLoading: false,
  isFetching: false,
  error: null,
  fetchedAt: null,
  subscribers: new Set(),
};

const isCacheStale = (): boolean =>
  storesCache.fetchedAt === null || Date.now() - storesCache.fetchedAt > CACHE_TTL_MS;

// Core loader. Fetches both datasets, joins, filters, sorts. While data already
// exists this runs as a silent background refresh (no spinner); stale data stays
// on screen and is replaced only on success — a failed refresh keeps it.
const loadStores = async () => {
  if (storesCache.isFetching) {
    return;
  }

  const hasData = !!storesCache.data;
  storesCache.isFetching = true;
  storesCache.isLoading = !hasData; // only show a spinner when there's nothing to show
  if (!hasData) {
    storesCache.error = null;
  }
  notifySubscribers();

  try {
    const [stores, storeControls] = await Promise.all([getAllStores(), getAllStoreControls()]);
    const statusByStoreNo = new Map<string, string | undefined>(
      storeControls.map((control) => [control.storeNo, control.storeStatus])
    );
    storesCache.data = filterAndSortStores(stores, statusByStoreNo);
    storesCache.fetchedAt = Date.now();
    storesCache.error = null;
  } catch (error) {
    if (storesCache.data) {
      // Background refresh failed — keep showing the cached data, stay quiet.
      console.error('Background store refresh failed; using cached data', error);
    } else {
      // Cold load failed — surface the error. A later remount retries (the
      // mount effect re-runs loadStores while data is still null).
      storesCache.error = error;
    }
  } finally {
    storesCache.isFetching = false;
    storesCache.isLoading = false;
    notifySubscribers();
  }
};

// Fetch only when needed: nothing in flight already, and either no data yet or
// the cached data has gone stale (background refresh).
const fetchStoresIfNeeded = () => {
  if (storesCache.isFetching) {
    return;
  }
  if (storesCache.data && !isCacheStale()) {
    return;
  }
  loadStores();
};

// Function to notify all subscribers of state changes
const notifySubscribers = () => {
  storesCache.subscribers.forEach(callback => callback());
};

// Force a refresh regardless of TTL (e.g. a manual "retry"/"refresh" action).
export const refetchStores = () => {
  loadStores();
};

// Function to reset the cache (clears everything; next access refetches)
export const resetStoresCache = () => {
  storesCache.data = null;
  storesCache.isLoading = false;
  storesCache.isFetching = false;
  storesCache.error = null;
  storesCache.fetchedAt = null;
  notifySubscribers();
};

// Hook to access shared stores data
export const useSharedStores = () => {
  const [, forceUpdate] = useState({});

  const triggerUpdate = useCallback(() => {
    forceUpdate({});
  }, []);

  useEffect(() => {
    // Add this component as a subscriber
    storesCache.subscribers.add(triggerUpdate);

    // Fetch stores if needed
    fetchStoresIfNeeded();

    // Cleanup: remove subscriber when component unmounts
    return () => {
      storesCache.subscribers.delete(triggerUpdate);
    };
  }, [triggerUpdate]);

  return {
    allStores: storesCache.data,
    isLoading: storesCache.isLoading,
    isFetching: storesCache.isFetching,
    error: storesCache.error,
    refetch: refetchStores,
  };
};